import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { enhancedServices } from "../../services/enhancedCrudService";
import Pagination from "../../components/Pagination";

const SanitarywareList = () => {
  const [sanitarywareProducts, setSanitarywareProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productCount, setProductCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const getProductImageUrl = (product) => {
    // Check if primary_image exists and has a valid URL
    if (product.primary_image && product.primary_image.url && product.primary_image.url !== null) {
      return product.primary_image.url;
    }
    // Check if images array exists and has valid URLs
    if (product.images && product.images.length > 0) {
      for (let i = 0; i < product.images.length; i++) {
        if (product.images[i] && product.images[i].url && product.images[i].url !== null) {
          return product.images[i].url;
        }
      }
    }
    return null;
  };

  const getProductTypeDisplay = (product) => {
    if (product.type && typeof product.type === "string") {
      return product.type;
    }
    if (product.type && typeof product.type === "number") {
      return `Type ${product.type}`;
    }
    return "N/A";
  };

  const fetchSanitarywareProducts = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: itemsPerPage,
        ordering: "-uploaded_at",
      };

      const response = await enhancedServices.sanitaryware.getList(params);

      if (
        response.results &&
        response.results.status === "success" &&
        response.results.products
      ) {
        setSanitarywareProducts(response.results.products || []);
        setProductCount(
          response.count || response.results.products.length || 0
        );
        setTotalPages(
          Math.ceil(
            (response.count || response.results.products.length || 0) /
              itemsPerPage
          )
        );
        setCurrentPage(page);
      } else {
        toast.error(
          response.message || "Failed to fetch sanitaryware products"
        );
      }
    } catch (error) {
      console.error("Error fetching sanitaryware products:", error);
      if (error.response?.data?.detail) {
        toast.error(`API Error: ${error.response.data.detail}`);
      } else {
        toast.error("Failed to fetch sanitaryware products");
      }
    } finally {
      setLoading(false);
    }
  };

  const openDetailModal = async (product) => {
    try {
       setLoading(true);
      if (!product.product_id) {
        setSelectedProduct(product);
        setDetailModal(true);
        toast.warning("Product ID is missing, showing basic details");
        return;
      }
      const detailResponse = await enhancedServices.sanitaryware.getDetail(
        product.product_id
      );

      if (detailResponse && detailResponse.status === "success") {
        setSelectedProduct(detailResponse);
      } else {
        setSelectedProduct(product);
        toast.warning(
          "Could not fetch detailed information, showing basic details"
        );
      }
      setDetailModal(true);
    } catch (error) {
      setSelectedProduct(product);
      setDetailModal(true);
      toast.warning(
        "Could not fetch detailed information, showing basic details"
      );
    } finally {
      setLoading(false);
    }
  };



  const handlePageChange = (page) => {
    fetchSanitarywareProducts(page);
  };


  const closeDetailModal = () => {
    setDetailModal(false);
    setSelectedProduct(null);
  };

  useEffect(() => {
    fetchSanitarywareProducts();
  }, []);

  return (
    <div>
      <div className="page-header mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">Sanitaryware Products</h3>
            <p className="text-muted mb-0">
              Manage sanitaryware products and their details.
            </p>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Products</h5>
              <span className="badge bg-primary">Total: {productCount}</span>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mt-3">
                    Loading sanitaryware products...
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th>Sr.No</th>
                        <th>Image</th>
                        <th>Name</th>
                        <th>Firm Name</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Published</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sanitarywareProducts.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-5">
                            <i className="fe fe-package fe-3x text-muted mb-3 d-block"></i>
                            <p className="text-muted mb-2">
                              No sanitaryware products found
                            </p>
                            <small className="text-muted">
                              Get started by creating your first sanitaryware
                              product
                            </small>
                          </td>
                        </tr>
                      ) : (
                        sanitarywareProducts.map((product, index) => (
                          <tr key={index}>
                            <td>
                              {index + 1 + (currentPage - 1) * itemsPerPage}
                            </td>
                            <td>
                              {(() => {
                                // Check if product has any valid images
                                const hasValidPrimaryImage = product.primary_image && product.primary_image.url && product.primary_image.url !== null;
                                const hasValidImages = product.images && product.images.length > 0 && product.images.some(img => img && img.url && img.url !== null);
                                
                                if (hasValidPrimaryImage || hasValidImages) {
                                  return (
                                    <img
                                      src={product.primary_image.url}
                                      alt="Product Image"
                                      style={{
                                        width: "60px",
                                        height: "40px",
                                        objectFit: "cover",
                                        borderRadius: "4px",
                                        border: "1px solid #dee2e6",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  );
                                } else {
                                  return (
                                    <div
                                    >
                                      N/A
                                    </div>
                                  );
                                }
                              })()}
                            </td>
                            <td>
                              <div>
                                <strong>{product.name}</strong>
                              </div>
                            </td>
                            <td>
                              <div>
                                <strong>
                                  {product.seller_firm_name ||
                                    "N/A"}
                                </strong>
                              </div>
                            </td>
                            <td>{getProductTypeDisplay(product)}</td>
                            <td>
                              <span
                                className={`badge ${
                                  product.is_active ? "bg-success" : "bg-danger"
                                }`}
                              >
                                {product.is_active ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  product.is_publish
                                    ? "bg-info"
                                    : "bg-secondary"
                                }`}
                              >
                                {product.is_publish ? "Published" : "Draft"}
                              </span>
                            </td>
                            <td>
                              <div className="actions d-flex flex-wrap gap-1">
                                <button
                                  className="btn btn-sm btn-outline-info"
                                  onClick={() => openDetailModal(product)}
                                  title="View Details"
                                >
                                  <i className="fe fe-eye"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                loading={loading}
                showPageInfo={true}
                className="mt-4"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {detailModal && selectedProduct && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Product Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeDetailModal}
                ></button>
              </div>
              <div className="modal-body">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-3">
                      Loading product details...
                    </p>
                  </div>
                ) : (
                  <div className="row">
                    <div className="col-12">
                      <div className="d-flex align-items-start gap-3 mb-3">
                        <img
                          src={getProductImageUrl(selectedProduct)}
                          alt={selectedProduct.name || "Product Image"}
                          className="img-thumbnail"
                          style={{
                            width: "120px",
                            height: "120px",
                            objectFit: "cover",
                            border: "2px solid #dee2e6",
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <div className="flex-grow-1">
                          <h4>{selectedProduct.name}</h4>
                          <p className="text-muted">
                            {selectedProduct.category} -{" "}
                            {getProductTypeDisplay(selectedProduct)}
                          </p>
                          <p className="text-muted mb-0">
                            Size: {selectedProduct.size || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="row mt-3">
                        <div className="col-sm-6">
                          <h6>Basic Information</h6>
                          <table className="table table-sm">
                            <tbody>
                              <tr>
                                <td>
                                  <strong>Product ID:</strong>
                                </td>
                                <td>{selectedProduct.product_id}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Grade:</strong>
                                </td>
                                <td>{selectedProduct.grade || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Color:</strong>
                                </td>
                                <td>{selectedProduct.color || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Glaze:</strong>
                                </td>
                                <td>{selectedProduct.glaze || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Quantity:</strong>
                                </td>
                                <td>{selectedProduct.quantity || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Weight:</strong>
                                </td>
                                <td>
                                  {selectedProduct.weight_kg
                                    ? `${selectedProduct.weight_kg} kg`
                                    : "N/A"}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="col-sm-6">
                          <h6>Additional Details</h6>
                          <table className="table table-sm">
                            <tbody>
                              <tr>
                                <td>
                                  <strong>Logo:</strong>
                                </td>
                                <td>{selectedProduct.logo || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Packing:</strong>
                                </td>
                                <td>{selectedProduct.packing || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Quantity Range:</strong>
                                </td>
                                <td>
                                  {selectedProduct.quantity_range || "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Seller Place:</strong>
                                </td>
                                <td>{selectedProduct.seller_place || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Loading Address:</strong>
                                </td>
                                <td>
                                  {selectedProduct.loading_address || "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Manufacturing Company:</strong>
                                </td>
                                <td>
                                  {selectedProduct.manufacturing_company_name ||
                                    "N/A"}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Attributes Section */}
                      {selectedProduct.attributes &&
                        selectedProduct.attributes.length > 0 && (
                          <div className="row mt-3">
                            <div className="col-12">
                              <h6>Product Attributes</h6>
                              <div className="row">
                                {selectedProduct.attributes.map(
                                  (attr, index) => (
                                    <div key={index} className="col-sm-6 mb-2">
                                      <div className="card">
                                        <div className="card-body p-2">
                                          <small className="text-muted">
                                            {attr.attribute_name}
                                          </small>
                                          <div className="fw-bold">
                                            {attr.value_name}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                      {/* Images Gallery */}
                      {selectedProduct.images &&
                        selectedProduct.images.length > 1 && (
                          <div className="row mt-3">
                            <div className="col-12">
                              <h6>Product Images</h6>
                              <div className="row">
                                {selectedProduct.images.map((image, index) => (
                                  <div key={index} className="col-sm-3 mb-2">
                                    <img
                                      src={image.url}
                                      alt={image.alt || selectedProduct.name}
                                      className="img-thumbnail"
                                      style={{
                                        width: "100%",
                                        height: "100px",
                                        objectFit: "cover",
                                        border: "1px solid #dee2e6",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                      {/* Seller Information Section */}
                      <div className="row mt-3">
                        <div className="col-12">
                          <h6>Seller Information</h6>
                          <div className="card">
                            <div className="card-body">
                              <div className="row">
                                <div className="col-sm-9">
                                  <table className="table table-sm mb-0">
                                    <tbody>
                                      <tr>
                                        <td style={{ width: "30%" }}>
                                          <strong>Firm Name:</strong>
                                        </td>
                                        <td>{selectedProduct.seller_firm_name || "N/A"}</td>
                                      </tr>
                                      <tr>
                                        <td>
                                          <strong>Mobile Number:</strong>
                                        </td>
                                        <td>{selectedProduct.seller_mobile_number || "N/A"}</td>
                                      </tr>
                                      <tr>
                                        <td>
                                          <strong>Location:</strong>
                                        </td>
                                        <td>{selectedProduct.seller_place || "N/A"}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row mt-3">
                        <div className="col-12">
                          <h6>Status</h6>
                          <div className="d-flex gap-2">
                            <span
                              className={`badge ${
                                selectedProduct.is_active
                                  ? "bg-success"
                                  : "bg-danger"
                              }`}
                            >
                              {selectedProduct.is_active
                                ? "Active"
                                : "Inactive"}
                            </span>
                            <span
                              className={`badge ${
                                selectedProduct.is_publish
                                  ? "bg-info"
                                  : "bg-secondary"
                              }`}
                            >
                              {selectedProduct.is_publish
                                ? "Published"
                                : "Draft"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeDetailModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showModal || detailModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
};

export default SanitarywareList;
